import moment from 'moment';
import 'moment-timezone';

import { EmailTypes, ScheduledEmail, SendTimeConfig, EmailScheduleBaseConfig, RelativeTime } from './types';

import { Option } from '@/studioComponents/StudioSelect.vue';
import { getCleanTimezoneAbbrev } from '@/utils/timezone';

export function sortEmailSchedule(emailSchedule: ScheduledEmail[]) {
  return emailSchedule.sort((a, b) => {
    if (a.email_type === 'OnDemand') {
      return 1;
    }
    if (b.email_type === 'OnDemand') {
      return -1;
    }

    const priorityTypes: Array<EmailTypes> = ['Waitlist', 'Registration', 'SpkInvite', 'StaffInvite'];
    const aInPriority = priorityTypes.indexOf(a.email_type);
    const bInPriority = priorityTypes.indexOf(b.email_type);

    if (aInPriority !== -1 && bInPriority !== -1) {
      return aInPriority - bInPriority;
    }

    if (aInPriority !== -1) {
      return -1;
    }
    if (bInPriority !== -1) {
      return 1;
    }

    return new Date(a?.schedule_time).getTime() - new Date(b?.schedule_time).getTime();
  });
}

export function getEmailTypeLabel(email_type: string): string {
  switch (email_type) {
    case 'Registration':
      return 'Registration';
    case 'Waitlist':
      return 'Waitlist';
    case 'Reminder-5m':
      return '5m Reminder';
    case 'Reminder-1h':
      return '1hr Reminder';
    case 'Reminder-4h':
      return '4hr Reminder';
    case 'Reminder-12h':
      return '12hr Reminder';
    case 'Reminder-1d':
      return '1 Day Reminder';
    case 'Reminder-2d':
      return '2 Day Reminder';
    case 'Reminder-7d':
      return '1 Week Reminder';
    case 'Post-Event-Attendee-1d':
      return '1 Day Post Event: Attended';
    case 'Post-Event-Non-Attendee-1d':
      return '1 Day Post Event: Not Attended';
    case 'OnDemand':
      return 'On Demand';
    case 'SpkInvite':
      return 'Speaker Instructions Email';
    case 'Spk-Reminder-1h':
      return 'Speaker 1hr Reminder';
    case 'Spk-Reminder-1d':
      return 'Speaker 1 Day Reminder';
    case 'StaffInvite':
      return 'Staff Instructions Email';
    case 'Staff-Reminder-1h':
      return 'Staff 1hr Reminder';
    case 'Staff-Reminder-1d':
      return 'Staff 1 Day Reminder';
    case 'Custom':
      return 'Custom';
    default:
      return 'Scheduled Email';
  }
}

export function getEmailTypeSubLabel(email_type: string): string {
  switch (email_type) {
    case 'SpkInvite':
      return 'When enabled, speaker instructions will be sent to speakers when they are added to the event.';
    case 'Spk-Reminder-1d':
      return 'When enabled, speaker instructions will be sent to speakers 24 hours before the event.';
    case 'Spk-Reminder-1h':
      return 'When enabled, speaker instructions will be sent to speakers 1 hour before the event.';
    default:
      return '';
  }
}

export function getEmailSendTime(
  email_type: EmailTypes,
  { start_time, end_time, timezone }: { start_time: string; end_time: string; timezone: string }
) {
  const startDateTime = moment(start_time);
  const endDateTime = moment(end_time);
  let send_time;

  // Offsets are applied in hours so the result is the exact instant the backend schedules. Calendar units
  // (days) keep the browser's wall-clock across its own DST change and drift 1h from schedule_time (GS-34789).
  switch (email_type) {
    case 'Registration':
      return 'Sends when a person is registered';
    case 'Waitlist':
      return 'Sends when a person is waitlisted';
    case 'OnDemand':
      return 'Sends when a person is registered for the event on demand';
    case 'Reminder-5m':
      send_time = moment(startDateTime);
      send_time.subtract(5, 'm');
      break;
    case 'Reminder-1h':
      send_time = moment(startDateTime);
      send_time.subtract(1, 'h');
      break;
    case 'Reminder-4h':
      send_time = moment(startDateTime);
      send_time.subtract(4, 'h');
      break;
    case 'Reminder-12h':
      send_time = moment(startDateTime);
      send_time.subtract(12, 'h');
      break;
    case 'Reminder-1d':
      send_time = moment(startDateTime);
      send_time.subtract(24, 'h');
      break;
    case 'Reminder-2d':
      send_time = moment(startDateTime);
      send_time.subtract(48, 'h');
      break;
    case 'Reminder-7d':
      send_time = moment(startDateTime);
      send_time.subtract(168, 'h');
      break;
    case 'Post-Event-Attendee-1d':
      send_time = moment(endDateTime);
      send_time.add(24, 'h');
      break;
    case 'Post-Event-Non-Attendee-1d':
      send_time = moment(endDateTime);
      send_time.add(24, 'h');
      break;
    case 'SpkInvite':
      return 'When a speaker is added to the event';
    case 'Spk-Reminder-1h':
      send_time = moment(startDateTime);
      send_time.subtract(1, 'h');
      break;
    case 'Spk-Reminder-1d':
      send_time = moment(startDateTime);
      send_time.subtract(24, 'h');
      break;
    case 'StaffInvite':
      return 'When a staff is added to the event';
    case 'Staff-Reminder-1h':
      send_time = moment(startDateTime);
      send_time.subtract(1, 'h');
      break;
    case 'Staff-Reminder-1d':
      send_time = moment(startDateTime);
      send_time.subtract(24, 'h');
      break;
    default:
      return '';
  }
  const timezoneName = String(timezone.split(' ')[0]);

  send_time.tz(timezoneName);
  const timezoneAbbrev = getCleanTimezoneAbbrev(send_time.format('z'), timezone);
  return send_time.format('MMMM D, YYYY - h:mma') + ' ' + timezoneAbbrev;
}

export function getEmailSendSnippet(email_type: EmailTypes) {
  switch (email_type) {
    case 'Registration':
      return 'Sends when registrant is created';
    case 'Waitlist':
      return 'Sends when registrant is waitlisted';
    case 'Reminder-5m':
      return 'Sends 5 minutes before the event';
    case 'Reminder-1h':
      return 'Sends 1 hour before the event';
    case 'Reminder-4h':
      return 'Sends 4 hours before the event';
    case 'Reminder-12h':
      return 'Sends 12 hours before the event';
    case 'Reminder-1d':
      return 'Sends 1 day before the event';
    case 'Reminder-2d':
      return 'Sends 2 days before the event';
    case 'Reminder-7d':
      return 'Sends 1 week before the event';
    case 'Post-Event-Attendee-1d':
      return 'Sends 1 day after the event to all attendees';
    case 'Post-Event-Non-Attendee-1d':
      return "Sends 1 day after the event to all registered users that didn't attend";
    case 'OnDemand':
      return 'Sends when a person is registered for the event on demand';
    case 'Spk-Reminder-1d':
      return 'Sends 1 day before the event';
    case 'Spk-Reminder-1h':
      return 'Sends 1 hour before the event';
    case 'SpkInvite':
      return 'Sends when a speaker is added to the event';
    case 'StaffInvite':
      return 'Sends when a staff is added to the event';
    case 'Staff-Reminder-1h':
      return 'Sends 1 hour before the event';
    case 'Staff-Reminder-1d':
      return 'Sends 1 day before the event';
    case 'Custom':
      return 'Sends at a custom-scheduled time';
    default:
      return '';
  }
}

export const EMAIL_TAGS = {
  QR_CODE: '##checkin_qrcode',
  LOCATION: '##event_location',
  MAGIC_LINK: '####'
};

export const CalendarInviteEmailTypes: EmailTypes[] = ['Registration', 'SpkInvite', 'StaffInvite'];

export const EMAIL_SCHEDULE_TIMING_OPTIONS = [
  {
    label: 'Before the event begins',
    value: '-EVENT_START'
  },
  {
    label: 'After the event begins',
    value: 'EVENT_START'
  },
  {
    label: 'Before the event ends',
    value: '-EVENT_END'
  },
  {
    label: 'After the event ends',
    value: 'EVENT_END'
  }
] as Array<Option>;

export const REGISTRANT_TARGET_OPTIONS: Array<Option> = [
  {
    label: 'All Registrants',
    value: 'ALL_REGISTRANTS'
  },
  {
    label: 'Event Attendees',
    value: 'ATTENDEES'
  },
  {
    label: 'Non-Attendees',
    value: 'NON_ATTENDEES'
  }
];

export const STAFF_TARGET_OPTIONS: Array<Option> = [
  {
    label: 'All Staff',
    value: 'STAFF'
  }
];

export const SPEAKER_TARGET_OPTIONS: Array<Option> = [
  {
    label: 'All Speakers',
    value: 'SPEAKERS'
  }
];

export function getEmailScheduleMessage(
  sendTime: SendTimeConfig,
  event: EmailScheduleBaseConfig,
  compactMode: boolean
): string {
  if (event.isRepeatingEvent || event.seriesId) {
    return '';
  }

  if (event.eventTemplateId) {
    const val = EMAIL_SCHEDULE_TIMING_OPTIONS.find(elm => elm.value === sendTime.relativeTime);
    const { weeks, days, hours, minutes } = sendTime;

    const timeParts: string[] = [];
    if (weeks > 0) {
      timeParts.push(`${weeks} ${weeks === 1 ? 'week' : 'weeks'}`);
    }
    if (days > 0) {
      timeParts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
    }
    if (hours > 0) {
      timeParts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
    }
    if (minutes > 0) {
      timeParts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
    }

    const timeString = timeParts.length > 0 ? timeParts.join(', ') : '0 minutes';

    return `Email will be sent ${timeString} ${val?.label.toLowerCase()}`;
  }

  const startTime = event.startTime;
  const endTime = event.endTime;
  const timezone = event.timezone;

  if (!startTime || !endTime || !timezone) {
    return '';
  }

  let timeRefVal = '';
  if (['-EVENT_START', 'EVENT_START'].includes(sendTime.relativeTime)) {
    timeRefVal = startTime;
  } else if (['-EVENT_END', 'EVENT_END'].includes(sendTime.relativeTime)) {
    timeRefVal = endTime;
  }

  // The offset is applied in minutes so the displayed time is the exact instant the backend stores in
  // schedule_time. Calendar units (weeks/days) keep the browser's wall-clock across its own DST change,
  // which showed UK users a send time 1h earlier than the real one (GS-34789).
  const finalDateTime = moment(timeRefVal)
    .add(calculationTimeInMins(sendTime), 'minutes')
    .tz(timezone.split(' ')[0] || '')
    .format('MMMM D, YYYY - h:mma z');

  if (compactMode) {
    return finalDateTime;
  }

  return `Email is scheduled to be sent on ${finalDateTime}`;
}

export function calculationTimeInMins(sendTime: SendTimeConfig): number {
  const { weeks, days, hours, minutes, relativeTime } = sendTime;

  const timeRef = relativeTime.startsWith('-') ? -1 : 1;

  return (weeks * 7 * 24 * 60 + days * 24 * 60 + hours * 60 + minutes) * timeRef;
}

export function getEmailReferencePoint(sendTime: SendTimeConfig): string {
  const { relativeTime } = sendTime;

  if (relativeTime.startsWith('-')) {
    return relativeTime.slice(1);
  }

  return relativeTime;
}

export function getDefaultScheduleTimeInMins(emailType: EmailTypes): {
  totalMinutes: number;
  relativeTime: string;
} {
  switch (emailType) {
    case 'Registration':
      return { totalMinutes: 0, relativeTime: 'EVENT_START' };
    case 'Waitlist':
      return { totalMinutes: 0, relativeTime: 'EVENT_START' };
    case 'Reminder-5m':
      return { totalMinutes: 5, relativeTime: '-EVENT_START' };
    case 'Reminder-1h':
      return { totalMinutes: 60, relativeTime: '-EVENT_START' };
    case 'Reminder-4h':
      return { totalMinutes: 240, relativeTime: '-EVENT_START' };
    case 'Reminder-12h':
      return { totalMinutes: 720, relativeTime: '-EVENT_START' };
    case 'Reminder-1d':
      return { totalMinutes: 1440, relativeTime: '-EVENT_START' };
    case 'Reminder-2d':
      return { totalMinutes: 2880, relativeTime: '-EVENT_START' };
    case 'Reminder-7d':
      return { totalMinutes: 10080, relativeTime: '-EVENT_START' };
    case 'Post-Event-Attendee-1d':
      return { totalMinutes: 1440, relativeTime: 'EVENT_END' };
    case 'Post-Event-Non-Attendee-1d':
      return { totalMinutes: 1440, relativeTime: 'EVENT_END' };
    case 'OnDemand':
      return { totalMinutes: 0, relativeTime: 'EVENT_START' };
    case 'Spk-Reminder-1d':
      return { totalMinutes: 1440, relativeTime: '-EVENT_START' };
    case 'Spk-Reminder-1h':
      return { totalMinutes: 60, relativeTime: '-EVENT_START' };
    case 'SpkInvite':
      return { totalMinutes: 0, relativeTime: '-EVENT_START' };
    case 'StaffInvite':
      return { totalMinutes: 0, relativeTime: '-EVENT_START' };
    case 'Staff-Reminder-1h':
      return { totalMinutes: 60, relativeTime: '-EVENT_START' };
    case 'Staff-Reminder-1d':
      return { totalMinutes: 1440, relativeTime: '-EVENT_START' };
    default:
      return { totalMinutes: 0, relativeTime: 'EVENT_START' };
  }
}

export function getScheduleTime(totalMinutes: number, savedRelativeTime: string): SendTimeConfig {
  const relativeTime = (totalMinutes < 0 ? `-${savedRelativeTime}` : savedRelativeTime) as RelativeTime;
  const totalMinutesAbs = Math.abs(totalMinutes);
  const weeks = Math.floor(totalMinutesAbs / (7 * 24 * 60));
  const days = Math.floor((totalMinutesAbs % (7 * 24 * 60)) / (24 * 60));
  const hours = Math.floor((totalMinutesAbs % (24 * 60)) / 60);
  const minutes = totalMinutesAbs % 60;
  return { weeks, days, hours, minutes, relativeTime };
}

export function validateCustomEmailSchedule(sendTime: SendTimeConfig, event: EmailScheduleBaseConfig): string {
  if (event.isRepeatingEvent || event.seriesId || event.eventTemplateId) {
    return '';
  }

  const startTime = event.startTime;
  const endTime = event.endTime;
  const timezone = event.timezone?.split(' ')[0] || '';

  if (!startTime || !endTime || !timezone) {
    return '';
  }
  let timeRefVal = '';
  if (['EVENT_START', '-EVENT_START'].includes(sendTime.relativeTime)) {
    timeRefVal = startTime;
  }

  if (['EVENT_END', '-EVENT_END'].includes(sendTime.relativeTime)) {
    timeRefVal = endTime;
  }

  const scheduledMoment = moment(timeRefVal).add(calculationTimeInMins(sendTime), 'minutes').tz(timezone);

  if (scheduledMoment < moment(event.startTime).subtract(180, 'days').tz(timezone)) {
    return 'Email Schedule Time cannot be before 180 days from event start time';
  }

  if (scheduledMoment > moment(event.endTime).add(30, 'days').tz(timezone)) {
    return 'Email Schedule Time cannot be more than 1 month from now';
  }

  if (scheduledMoment < moment().tz(timezone)) {
    return 'Email Schedule Time cannot be in the past';
  }

  if (scheduledMoment < moment().add(30, 'minutes').tz(timezone)) {
    return 'Email Schedule Time cannot be less than 30 minutes from now';
  }

  return '';
}

export const getErrorMessages = (error: string, fallbackMessage: string): string => {
  try {
    const parsedError = JSON.parse(error);
    if (parsedError?.message) {
      return parsedError.message;
    }

    for (const key of Object.keys(parsedError)) {
      if (parsedError[key]) {
        return parsedError[key]?.[0]?.message || fallbackMessage;
      }
    }
    return fallbackMessage;
  } catch (e) {
    return fallbackMessage;
  }
};
